import { useEffect, useState } from 'react';
import { fetchMe, type Me } from '../utils/apiClient';

/**
 * Shared `/me` cache + subscription. Exactly one `/me` request is sent on
 * app mount; every subsequent `useMe()` reader subscribes to updates via the
 * module-level listener set. Any Phase-7 settings mutation that could change
 * the caller's own profile/memberships calls `refreshMe()` to invalidate.
 *
 * Hard-refresh perf: `/me` is also mirrored in `localStorage['me.v1']`. On
 * module load we hydrate the cache synchronously from storage so the sidebar
 * + Settings-link gate render correctly from the first paint; the background
 * fetch still runs and overwrites the cache with fresh data. Storage entries
 * older than 7 days are ignored to bound staleness.
 *
 * See ADR 0009 §4 + §7.
 */

const STORAGE_KEY = 'me.v1';
const MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredMe {
  me: Me;
  at: number;
}

function loadFromStorage(): Me | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredMe;
    if (!parsed?.me || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > MAX_STALE_MS) return null;
    return parsed.me;
  } catch {
    return null;
  }
}

function saveToStorage(me: Me | null): void {
  try {
    if (!me) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ me, at: Date.now() } satisfies StoredMe));
    }
  } catch {
    // Storage quota or private mode — non-fatal.
  }
}

export type { Me, MeGroup } from '../utils/apiClient';

const listeners = new Set<() => void>();
let cache: Me | null = loadFromStorage();
let inflight: Promise<void> | null = null;
// Session-scoped: `/me` is fetched once per tab, not once per component mount.
// Without this, a remount storm (see 2026-04-24 blinking bug) fires /me on
// every subscribing component's mount, hammering the API. `clearMeCache`
// resets the flag so a next sign-in refetches.
let hasInitialized = false;

export async function refreshMe(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      cache = await fetchMe();
      saveToStorage(cache);
      for (const l of listeners) l();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Called on sign-out so the next user doesn't inherit the prior cache. */
export function clearMeCache(): void {
  cache = null;
  hasInitialized = false;
  saveToStorage(null);
  for (const l of listeners) l();
}

export function useMe(): { me: Me | null; refresh: () => Promise<void> } {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (): void => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  useEffect(() => {
    if (hasInitialized) return;
    hasInitialized = true;
    if (!inflight) {
      void refreshMe().catch(() => {
        // Let the next mount retry rather than lock the session into a failed state.
        hasInitialized = false;
      });
    }
  }, []);
  return { me: cache, refresh: refreshMe };
}
